import { videos, type Video } from "@/mocks/videos";
import { categories, type CategoryKey } from "@/mocks/categories";

export type SortKey = "latest" | "hot" | "rating";

export type VideoQuery = {
  category?: CategoryKey;
  year?: string;
  region?: string;
  keyword?: string;
  sort?: SortKey;
};

export function getCategory(key: string) {
  return categories.find((item) => item.key === key);
}

export function getCategoryName(key: string): string {
  return getCategory(key)?.name ?? "全部视频";
}

export function formatCount(value: number): string {
  if (value >= 100000000) {
    return `${(value / 100000000).toFixed(1)}亿`;
  }
  if (value >= 10000) {
    return `${(value / 10000).toFixed(1)}万`;
  }
  return `${value}`;
}

export function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, "0")}-${`${date.getDate()}`.padStart(2, "0")}`;
}

export function getAllVideos(): Video[] {
  return [...videos];
}

export function getVideoById(id: string): Video | undefined {
  return videos.find((item) => item.id === id);
}

export function getVideosByCategory(key: CategoryKey): Video[] {
  if (key === "all") return [...videos];
  return videos.filter((item) => item.category === key);
}

export function getLatestVideos(limit?: number): Video[] {
  const list = [...videos].sort(
    (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
  );
  return typeof limit === "number" ? list.slice(0, limit) : list;
}

export function getHotVideos(limit?: number): Video[] {
  const list = [...videos].sort((a, b) => b.views - a.views);
  return typeof limit === "number" ? list.slice(0, limit) : list;
}

export function queryVideos({
  category = "all",
  year = "全部",
  region = "全部",
  keyword = "",
  sort = "latest",
}: VideoQuery): Video[] {
  const normalizedKeyword = keyword.trim().toLowerCase();

  const result = videos.filter((item) => {
    if (category !== "all" && item.category !== category) return false;
    if (year !== "全部" && `${item.year}` !== year) return false;
    if (region !== "全部" && item.region !== region) return false;
    if (normalizedKeyword) {
      const haystack = [item.title, item.summary, item.director, ...item.tags]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(normalizedKeyword)) return false;
    }
    return true;
  });

  if (sort === "hot") {
    return result.sort((a, b) => b.views - a.views);
  }
  if (sort === "rating") {
    return result.sort((a, b) => b.rating - a.rating);
  }
  return result.sort(
    (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
  );
}

export function getRelatedVideos(video: Video, limit = 4): Video[] {
  const scored = videos
    .filter((item) => item.id !== video.id)
    .map((item) => {
      let score = 0;
      if (item.category === video.category) score += 3;
      score += item.tags.filter((tag) => video.tags.includes(tag)).length * 2;
      if (item.region === video.region) score += 1;
      if (item.year === video.year) score += 1;
      return { item, score };
    })
    .sort((a, b) => b.score - a.score || b.item.views - a.item.views);

  return scored.slice(0, limit).map((entry) => entry.item);
}